import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

import { AnchoredPopover } from '../AnchoredPopover';
import { FieldError } from './FieldError';
import { FIELD_CLASSES, fieldWidth } from './fieldStyles';
import { resolveTyped } from './parseFields';

export interface ComboOption {
  value: number;
  sub?: string;
}

interface ComboFieldProps {
  value: number | null;
  display: (value: number) => string;
  options: ComboOption[];
  parse: (text: string) => number | null;
  onChange: (value: number | null) => void;
  label: string;
  placeholder?: string;
  icon?: React.ReactNode;
  clearable?: boolean;
  ltr?: boolean;
  width?: number;
  listWidth?: number;
  disabled?: boolean;
}

const nearestIndex = (options: ComboOption[], target: number | null): number => {
  if (target === null) return -1;
  return options.reduce(
    (best, option, index) =>
      best < 0 || Math.abs(option.value - target) < Math.abs(options[best].value - target) ? index : best,
    -1,
  );
};

export const ComboField: React.FC<ComboFieldProps> = ({
  value,
  display,
  options,
  parse,
  onChange,
  label,
  placeholder,
  icon,
  clearable = false,
  ltr = false,
  width = 140,
  listWidth,
  disabled = false,
}) => {
  const { t } = useTranslation('planner');
  const listId = useId();
  const wrapRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<string | null>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [draft, setDraftState] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [rejected, setRejected] = useState(false);
  const [unreadable, setUnreadable] = useState<string | null>(null);
  const errorId = useId();

  const setDraft = (next: string | null) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const typed = draft !== null && draft.trim() ? parse(draft) : null;
  const near = nearestIndex(options, typed ?? value);
  const highlighted = active >= 0 ? active : near;

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${highlighted}"]`);
    if (anchor && node && listRef.current) listRef.current.scrollTop = node.offsetTop - 96;
  }, [anchor, highlighted]);

  const open = () => {
    if (wrapRef.current) setAnchor(wrapRef.current.getBoundingClientRect());
  };
  const close = () => {
    setAnchor(null);
    setActive(-1);
  };
  const pick = (next: number | null) => {
    setDraft(null);
    setUnreadable(null);
    close();
    if (next !== value) onChange(next);
    inputRef.current?.blur();
  };
  const commit = () => {
    const text = draftRef.current;
    if (text === null) return;
    setDraft(null);
    const result = resolveTyped(text, value, parse, clearable);
    if (result.rejected) {
      setRejected(true);
      setUnreadable(text.trim());
      setTimeout(() => setRejected(false), 400);
      return;
    }
    setUnreadable(null);
    if (result.value !== value) onChange(result.value);
  };
  const shown = (next: number) => (ltr ? <bdi dir="ltr">{display(next)}</bdi> : display(next));

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!anchor) open();
      setDraft(null);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive(Math.max(0, Math.min(options.length - 1, (highlighted < 0 ? -1 : highlighted) + step)));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (active >= 0 && draftRef.current === null) pick(options[active].value);
      else {
        commit();
        close();
        inputRef.current?.blur();
      }
    }
  };

  return (
    <span className="inline-flex min-w-0 max-w-full flex-col gap-1">
      <label
        ref={wrapRef}
        style={{ width: fieldWidth(width) }}
        className={cn(FIELD_CLASSES, rejected && 'animate-shake motion-reduce:animate-none', disabled && 'opacity-60')}
      >
        {icon}
        <input
          ref={inputRef}
          value={draft ?? (value === null ? '' : display(value))}
          placeholder={placeholder}
          aria-label={label}
          role="combobox"
          aria-expanded={!!anchor}
          aria-controls={anchor ? listId : undefined}
          aria-activedescendant={anchor && highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
          aria-invalid={unreadable !== null || undefined}
          aria-describedby={unreadable !== null ? errorId : undefined}
          autoComplete="off"
          disabled={disabled}
          dir={ltr ? 'ltr' : undefined}
          className="min-w-0 flex-1 bg-transparent text-start outline-none placeholder:text-theme-text-tertiary"
          onFocus={(event) => {
            open();
            event.target.select();
          }}
          onClick={() => !anchor && open()}
          onChange={(event) => {
            setDraft(event.target.value);
            setUnreadable(null);
            setActive(-1);
            if (!anchor) open();
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            commit();
            close();
          }}
        />
        {clearable && value !== null && !disabled && (
          <button
            type="button"
            aria-label={t('fields.clear')}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              pick(null);
            }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-theme-text-tertiary hover:text-theme-text-primary"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </label>
      {anchor && (
        <AnchoredPopover
          anchor={anchor}
          width={listWidth ?? Math.max(anchor.width, 156)}
          label={label}
          ignoreRef={wrapRef}
          onClose={close}
          onEscape={() => {
            setDraft(null);
            close();
            inputRef.current?.blur();
          }}
          className="p-1.5"
        >
          <div role="presentation" onMouseDown={(event) => event.preventDefault()}>
            {typed !== null && !options.some((option) => option.value === typed) && (
              <button
                type="button"
                onClick={() => pick(typed)}
                className="flex h-9 w-full items-center justify-between rounded-md px-2.5 text-[14px] text-theme-text-primary hover:bg-theme-card-hover"
              >
                <span>{shown(typed)}</span>
                <kbd className="rounded border border-theme-border-light px-1.5 text-[11px] text-theme-text-tertiary">
                  {t('fields.enter')}
                </kbd>
              </button>
            )}
            <div ref={listRef} id={listId} role="listbox" aria-label={label} className="max-h-60 overflow-y-auto">
              {options.map((option, index) => (
                <button
                  key={option.value}
                  id={`${listId}-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  data-index={index}
                  aria-selected={option.value === value}
                  onClick={() => pick(option.value)}
                  className={cn(
                    'flex h-9 w-full items-center gap-2 rounded-md px-2.5 text-start text-[14px] text-theme-text-primary hover:bg-theme-card-hover',
                    index === highlighted && 'bg-theme-card-hover',
                    option.value === value && 'font-semibold',
                  )}
                >
                  <span className="flex-1">{shown(option.value)}</span>
                  {option.sub && <small className="text-[12px] text-theme-text-tertiary">{option.sub}</small>}
                  {option.value === value && (
                    <Check className="h-3.5 w-3.5 text-theme-accent-primary" strokeWidth={2.6} aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </AnchoredPopover>
      )}
      {unreadable !== null && <FieldError id={errorId} message={t('fields.unreadable', { text: unreadable })} />}
    </span>
  );
};
